<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div v-if="isUpcoming" class="orangehrm-buzz-event-countdown">
    <oxd-text tag="span" class="orangehrm-buzz-event-countdown-label">
      {{ labelText }}
    </oxd-text>
    <oxd-text tag="span" class="orangehrm-buzz-event-countdown-timer">
      {{ timerText }}
    </oxd-text>
  </div>
</template>

<script>
import {computed, onUnmounted, ref, watch} from 'vue';
import usei18n from '@/core/util/composable/usei18n';
import {parseDate} from '@/core/util/helper/datefns';

const padUnit = (value) => String(value).padStart(2, '0');

export default {
  name: 'EventCountdownLabel',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const {$t} = usei18n();
    const now = ref(Date.now());
    let intervalId;

    const eventTimestamp = computed(() => {
      const utcDate = parseDate(
        `${props.event.eventDate} ${props.event.eventTime} +00:00`,
        'yyyy-MM-dd HH:mm xxx',
      );
      return utcDate.getTime();
    });

    const remainingMs = computed(() => eventTimestamp.value - now.value);

    const isUpcoming = computed(() => remainingMs.value > 0);

    const timerText = computed(() => {
      const totalSeconds = Math.floor(remainingMs.value / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${padUnit(days)}:${padUnit(hours)}:${padUnit(minutes)}:${padUnit(
        seconds,
      )}`;
    });

    const labelText = computed(() => $t('buzz.event_countdown_label'));

    intervalId = window.setInterval(() => {
      now.value = Date.now();
    }, 1000);

    watch(isUpcoming, (upcoming) => {
      if (!upcoming && intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    });

    onUnmounted(() => {
      window.clearInterval(intervalId);
    });

    return {
      isUpcoming,
      labelText,
      timerText,
    };
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-buzz-event-countdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: calc(#{$oxd-border-radius} * -1);
  padding: 0.5rem 1.2rem calc(0.5rem + #{$oxd-border-radius});
  border-radius: $oxd-border-radius $oxd-border-radius 0 0;
  background-color: $oxd-primary-one-color;
  font-family: $oxd-font-family;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  z-index: 0;

  &-label,
  &-timer {
    margin: 0;
    color: $oxd-primary-font-color;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
  }

  &-timer {
    flex-shrink: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
    text-transform: none;
    letter-spacing: 0.08em;
  }
}
</style>
